import React, { useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import StepShell from './StepShell';

import { FormikDatePickerField } from 'CommonComponents/FormikDatePickerField';

import { useToast } from 'Hooks/useToast';

const FIELDS = ['start_date', 'end_date', 'event_start_date', 'event_end_date', 'cashflow_date'];

/**
 * Serialises to the Y-m-d the endpoint validates, taking the calendar day the
 * user picked rather than the UTC one — toISOString() shifts the date by a day
 * for anyone west of Greenwich.
 */
function toIsoDate(value) {
    if (!value) {
        return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
}

/**
 * The bracketing rules, mirrored from the server's ProjectDateRules so the
 * form can show which rule is broken as the user types. The server remains the
 * gate — these messages match its wording.
 *
 * Bounds are inclusive at both ends, so the comparisons use min/max rather
 * than Yup's exclusive date ordering.
 */
/**
 * An unset date is held as null, never '': yup's date transform turns '' into
 * an Invalid Date, which fails the type check before required() is reached and
 * surfaces yup's own untranslated message naming the database column. nullable()
 * lets null through the type check so required() owns the empty case.
 *
 * Invalid Date is also `instanceof Date`, so the bracketing comparisons below
 * test for a usable date rather than the constructor.
 */
const isRealDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const dateField = () => Yup.date().nullable().typeError($t('Enter a valid date.')).required($t('This value is required'));

const DatesSchema = Yup.object().shape({
    start_date: dateField().when('event_start_date', (eventStartDate, schema) =>
        isRealDate(eventStartDate) ? schema.max(eventStartDate, $t('The organisation start date must be on or before the event start date.')) : schema,
    ),
    event_start_date: dateField(),
    event_end_date: dateField().when('event_start_date', (eventStartDate, schema) =>
        isRealDate(eventStartDate) ? schema.min(eventStartDate, $t('The event end date must be on or after the event start date.')) : schema,
    ),
    end_date: dateField().when('event_end_date', (eventEndDate, schema) =>
        isRealDate(eventEndDate) ? schema.min(eventEndDate, $t('The organisation end date must be on or after the event end date.')) : schema,
    ),
    cashflow_date: dateField()
        .when('start_date', (startDate, schema) =>
            isRealDate(startDate) ? schema.min(startDate, $t('The default cashflow date must be on or after the organisation start date.')) : schema,
        )
        .when('end_date', (endDate, schema) =>
            isRealDate(endDate) ? schema.max(endDate, $t('The default cashflow date must be on or before the organisation end date.')) : schema,
        ),
});

/**
 * Explanations sit above each input at a fixed height so the five inputs line
 * up across the row — they replace the tooltip icons the old screen used.
 */
function DateField({ label, explanation, name, disabled, error }) {
    return (
        <div className="tw-flex tw-flex-col">
            <label htmlFor={name} className="text-input__label">
                {label} *
            </label>
            <p className="tw-mt-1 tw-mb-2 tw-h-16 tw-text-2xs tw-text-charcoal-50">{explanation}</p>
            <FormikDatePickerField name={name} id={name} disabled={disabled} className="text-input" />
            {error ? <div className="tw-mt-1.5 tw-text-red-500 tw-text-sm">{Array.isArray(error) ? error.join(' ') : error}</div> : null}
        </div>
    );
}

export default function DatesStep({ step, urls, deferring, onDefer, onSaved }) {
    const [data, setData] = useState(null);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => setData(data))
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    if (!data) {
        return (
            <StepShell step={step} deferring={deferring} saving={true} onDefer={onDefer} onNext={() => {}} nextLabel={$t('Save and continue')}>
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            </StepShell>
        );
    }

    const initialValues = FIELDS.reduce((values, field) => ({ ...values, [field]: data[field] ? new Date(data[field]) : null }), {});

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={DatesSchema}
            // A project saved before the rule was tightened must open with the
            // error already visible and the save blocked, not only after a
            // first edit.
            validateOnMount={true}
            initialTouched={FIELDS.reduce((touched, field) => ({ ...touched, [field]: true }), {})}
            onSubmit={(values, actions) => {
                const payload = FIELDS.reduce((body, field) => ({ ...body, [field]: toIsoDate(values[field]) }), {});

                axios
                    .put(urls.update, payload)
                    .then(({ data }) => onSaved(data))
                    .catch((e) => {
                        if (e.response?.data?.errors) {
                            actions.setErrors(e.response.data.errors);
                        } else {
                            toast.error($t('Something went wrong. Please try again.'));
                        }
                    })
                    .finally(() => actions.setSubmitting(false));
            }}
        >
            {({ errors, isSubmitting, isValid, handleSubmit }) => (
                <StepShell
                    step={step}
                    deferring={deferring}
                    saving={isSubmitting}
                    onDefer={onDefer}
                    onNext={handleSubmit}
                    nextDisabled={step.read_only || !isValid}
                    nextLabel={$t('Save and continue')}
                >
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-x-6 tw-gap-y-4">
                        <DateField
                            label={$t('Organisation start date')}
                            explanation={$t(
                                'The first month your budget and cashflow can contain figures. Usually when the organising entity begins operating.',
                            )}
                            name="start_date"
                            disabled={step.read_only}
                            error={errors.start_date}
                        />
                        <DateField
                            label={$t('Organisation end date')}
                            explanation={$t('The last month of budget and cashflow, typically dissolution or final acquittal.')}
                            name="end_date"
                            disabled={step.read_only}
                            error={errors.end_date}
                        />
                        <DateField
                            label={$t('Event start date')}
                            explanation={$t(
                                'Defines the event window, a narrower period inside the organisation date range, used to allocate event timing windows to venues.',
                            )}
                            name="event_start_date"
                            disabled={step.read_only}
                            error={errors.event_start_date}
                        />
                        <DateField
                            label={$t('Event end date')}
                            explanation={$t('The close of the event window.')}
                            name="event_end_date"
                            disabled={step.read_only}
                            error={errors.event_end_date}
                        />
                        <DateField
                            label={$t('Default cashflow date')}
                            explanation={$t("Where a budget line's spend sits if it has not been phased manually.")}
                            name="cashflow_date"
                            disabled={step.read_only}
                            error={errors.cashflow_date}
                        />
                    </div>

                    <p className="tw-mt-6 tw-text-2xs tw-text-charcoal-50">
                        {$t(
                            'Organisation dates must bracket the event dates, and the default cashflow date must fall inside the organisation window. Errors show inline as you type, not after saving.',
                        )}
                    </p>
                </StepShell>
            )}
        </Formik>
    );
}
